import { DeleteOutline, DriveFileRenameOutline, Search, UploadFile } from "@mui/icons-material";
import { Box, Button, CircularProgress, IconButton, InputAdornment, Paper, TextField, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteMedia, getMedia, MediaAsset, renameMedia, uploadMedia } from "../../service/MediaService";
import { showSuccessToast } from "../../utils/Toast";

/**
 * Mediebiblioteket: alle billeder ét sted, så det samme billede kan bruges i en mail, på et spil og på et
 * level uden at skulle findes frem igen.
 *
 * Bruges to steder med samme kode: som side (Medier) og som vælger i en dialog (`onPick`). Det er med vilje -
 * så opfører de to sig ens, og der er kun ét sted at rette.
 */

interface Props {
  /** Sat = biblioteket bruges til at vælge et billede, og hvert billede kan klikkes */
  onPick?: (asset: MediaAsset) => void;
  /** Sat = værktøjerne til at rette navn og slette vises (på siden, ikke i vælgeren) */
  manage?: boolean;
}

const size = (bytes: number) => (bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`);

export default function MediaLibrary({ onPick, manage = false }: Props) {
  const [t] = useTranslation("global");
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const fileField = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async (term: string) => {
    const answer = await getMedia(term);
    setAssets(answer.results);
    setTotal(answer.total);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => load(search), 300);
    return () => clearTimeout(timer);
  }, [search, load]);

  const add = async (files: FileList | File[] | null) => {
    if (!files || !files.length) return;
    setBusy(true);
    for (const file of Array.from(files)) {
      // eslint-disable-next-line no-await-in-loop
      await uploadMedia(file);
    }
    setBusy(false);
    await load(search);
    showSuccessToast(t("media.uploaded"), false);
  };

  const remove = async (asset: MediaAsset) => {
    if (await deleteMedia(asset.id)) {
      showSuccessToast(t("media.deleted"), false);
      await load(search);
    }
  };

  const rename = async (asset: MediaAsset) => {
    // eslint-disable-next-line no-alert
    const name = window.prompt(t("media.renamePrompt"), asset.name);
    if (!name || name === asset.name) return;
    if (await renameMedia(asset.id, name)) await load(search);
  };

  return (
    <Box
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes("Files")) {
          event.preventDefault();
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        add(event.dataTransfer.files);
      }}
      sx={{
        borderRadius: 2,
        p: 1,
        border: "2px dashed",
        borderColor: over ? "primary.main" : "transparent",
        bgcolor: over ? "action.hover" : "transparent",
        transition: "background-color .12s, border-color .12s",
      }}
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
        <TextField
          size="small"
          placeholder={t("media.search")}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ flex: "1 1 240px" }}
        />
        <Button variant="contained" component="label" startIcon={<UploadFile />} disabled={busy}>
          {t("media.upload")}
          <input
            hidden
            multiple
            type="file"
            ref={fileField}
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={(event) => add(event.target.files)}
          />
        </Button>
        {busy && <CircularProgress size={22} />}
        <Typography variant="body2" color="text.secondary">
          {t("media.count", { count: total })}
        </Typography>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {t("media.help")}
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 2 }}>
        {assets.map((asset) => (
          <Paper
            key={asset.id}
            variant="outlined"
            onClick={() => onPick?.(asset)}
            sx={{
              p: 1,
              borderRadius: 2,
              cursor: onPick ? "pointer" : "default",
              "&:hover": onPick ? { borderColor: "primary.main" } : {},
            }}
          >
            <Box
              component="img"
              src={asset.posterUrl || asset.url}
              alt={asset.name}
              loading="lazy"
              sx={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 1, display: "block", bgcolor: "action.hover" }}
            />
            <Typography variant="body2" noWrap title={asset.name} sx={{ mt: 0.5 }}>
              {asset.name}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                {asset.width ? `${asset.width}×${asset.height} · ` : ""}
                {size(asset.sizeBytes)}
              </Typography>
              {manage && (
                <>
                  <Tooltip title={t("media.rename")}>
                    <IconButton size="small" onClick={(event) => { event.stopPropagation(); rename(asset); }}>
                      <DriveFileRenameOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("media.delete")}>
                    <IconButton size="small" onClick={(event) => { event.stopPropagation(); remove(asset); }}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Paper>
        ))}
      </Box>

      {assets.length === 0 && !busy && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderStyle: "dashed" }}>
          <Typography variant="body2" color="text.secondary">
            {search ? t("media.noMatch") : t("media.empty")}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
